/**
 * WS/HTTP credential resolution for the Alex tab.
 *
 * SECURITY: the ChatRoom DO websocket and the direct storage-gateway HTTP calls must present the REAL caller
 * credential, never the public static API token (worthless as an authZ signal). It is the Firebase ID token for authed
 * users, and for guests a signed guest session token minted by guest-chat-create. On a guest's first session that
 * token is absent until create returns it, so the credential resolves to null, NO bearer is sent, and the WS connect is SKIPPED.
 */

import { isBrowser } from '@federa/utils/browser/detection';
import { isGuestUser } from '@federa/utils/strings/user-id';
import { GUEST_ID_PREFIX, GUEST_SESSION_TOKEN_STORAGE_KEY } from '@federa/const/auth-session';
import { LOGGER_TAGS } from '@federa/const/logging';
import { scopedLogger } from '@federa/utils/deps/registry';
import { extractError } from '@federa/utils/errors/extract-error';
import { readStoredGuestSessionToken } from '@federa/utils/auth/guest-session-token-storage';
import { readBridgeIdToken } from '@federa/components/lib/api/bridge-id-token';
import { readCachedAssistantTicket } from '@federa/utils/assistant/assistant-room-cache';

const log = scopedLogger(LOGGER_TAGS.MESSAGING_CLIENT);

function isGuestIdentity(userId: string, username: string | undefined): boolean {
	return userId.startsWith(GUEST_ID_PREFIX) || isGuestUser(username ?? '');
}

/** Persist the guest session token returned by guest-chat-create so it survives reload. */
export function persistGuestSessionToken(token: string): void {
	if (!isBrowser() || !token) return;
	try {
		window.localStorage.setItem(GUEST_SESSION_TOKEN_STORAGE_KEY, token);
	} catch (err) {
		log.error('Failed to persist guest session token', extractError(err));
	}
}

/**
 * The authenticated user's Firebase ID token, through the shared-bridge SSOT `readBridgeIdToken`
 * (`components/lib/api/bridge-id-token.ts`) -- which this module used to respell WITHOUT its `authStateReady()` await, so a
 * mount-time read raced session restoration and answered null while the user was in fact signed in.
 *
 * `forceRefresh` mints a new token instead of returning the SDK's cached one. Callers pass it on RETRY, because a
 * cached token that the SDK will not refresh on its own (expired, or an auth instance left in a bad state by an earlier
 * session) otherwise makes every retry present the identical rejected credential -- an unbreakable reconnect loop that
 * only a log-out/log-in clears.
 *
 * THE CATCH LIVES HERE, NOT IN THE SSOT. A refusal must read as "no credential" for the assistant, because
 * `resolveWsCredential`'s callers decide whether to connect off a `string | null` and a rejection escaping into the
 * reconnect scheduler would abort the retry rather than defer it. The SSOT deliberately propagates instead, so a
 * one-shot fetch client still sees its own failure; folding this catch into it would erase that.
 */
export async function readFirebaseIdToken(forceRefresh = false): Promise<string | null> {
	try {
		return await readBridgeIdToken({ forceRefresh });
	} catch (err) {
		log.error('Failed to read Firebase ID token', extractError(err));
		return null;
	}
}

/** Resolve the WS/HTTP bearer credential: the persisted guest session token for guests, else the authenticated user's Firebase ID token. Returns null when no real credential is available (never the static token). */
export async function resolveWsCredential(userId: string, username: string | undefined, forceRefresh = false, roomId?: string): Promise<string | null> {
	// Guests are unaffected by forceRefresh: their credential is a persisted
	// session token, not a mintable JWT.
	if (isGuestIdentity(userId, username)) return readStoredGuestSessionToken();
	// A named user's ROOM TICKET first (`room/create` minted it for this viewer and room): one HMAC at the upgrade
	// instead of the Firebase JWT plus two D1 lookups. Never on a RETRY -- a retry follows a presented credential the
	// gate rejected, and a ticket that was rejected (expired, secret rotated) must give way to the Firebase token.
	if (!forceRefresh && roomId) {
		const ticket = readCachedAssistantTicket({ userId, roomId });
		if (ticket) return ticket;
	}
	return readFirebaseIdToken(forceRefresh);
}
